import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FuseConfigService } from '@fuse/services/config';
import {
  CreateExpertManagerRequest,
  ExpertManagerRecord,
  ExpertManagerService,
  WebColorScheme,
} from '@libs/shared/api';
import { BasicSnackbarService } from '@libs/shared/ui/basic-snackbar/basic-snackbar.service';
import { convertToFuseScheme } from '@libs/shared/utils/functions/convert-to-fuse-scheme';
import { extractErrorMessage } from '@libs/shared/utils/functions/error-message.util';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, Subject, switchMap } from 'rxjs';

interface ExpertManagerCoreState {
  content: ExpertManagerRecord;
  isLoading: boolean;
  isLoaded: boolean;
  errorMessage: string;
}

const initialState: ExpertManagerCoreState = {
  content: undefined,
  isLoading: false,
  isLoaded: false,
  errorMessage: '',
};

@Injectable({
  providedIn: 'root',
})
export class CurrentExpertManagerCoreStore {
  readonly #expertManagerService = inject(ExpertManagerService);
  readonly #basicSnackbarService = inject(BasicSnackbarService);
  readonly #router = inject(Router);
  readonly #fuseConfigService = inject(FuseConfigService);

  readonly state$$ = signalState(initialState);
  readonly content$$ = this.state$$.content;
  readonly isLoading$$ = this.state$$.isLoading;
  readonly isLoaded$$ = this.state$$.isLoaded;
  readonly errorMessage$$ = this.state$$.errorMessage;

  constructor() {}

  readonly created$ = new Subject<ExpertManagerRecord>();
  readonly updated$ = new Subject<ExpertManagerRecord>();

  fetchExpertManagerById = rxMethod<{
    id: string;
    eventType?: 'created' | 'updated';
  }>(
    pipe(
      switchMap(payload =>
        this.#expertManagerService.fetchExpertManagerById(payload.id).pipe(
          tapResponse(
            (expertManager: ExpertManagerRecord) => {
              patchState(this.state$$, {
                content: expertManager,
                isLoading: false,
                isLoaded: true,
                errorMessage: '',
              });

              this.#fuseConfigService.config = {
                scheme: convertToFuseScheme(expertManager.webColorScheme),
              };

              switch (payload.eventType) {
                case 'created':
                  this.created$.next(expertManager);
                  break;
                case 'updated':
                  this.updated$.next(expertManager);
                  break;
              }
            },
            (e: HttpErrorResponse) => {
              if (e.status === 404) {
                this.#router.navigate(['/on-boarding']);
              }
              patchState(this.state$$, {
                content: undefined,
                isLoading: false,
                isLoaded: false,
                errorMessage: extractErrorMessage(e),
              });
            }
          )
        )
      )
    )
  );

  createExpertManager = rxMethod<{
    request: CreateExpertManagerRequest;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.#expertManagerService.createExpertManager(payload.request).pipe(
          tapResponse(
            (expertManagerId: string) => {
              if (payload.showSuccessSnackbar) {
                this.#basicSnackbarService.open('코치 매니저가 생성 되었어요.');
              }
              this.fetchExpertManagerById({
                id: expertManagerId,
                eventType: 'created',
              });
            },
            (e: HttpErrorResponse) => {
              if (payload.showFailureSnackbar) {
                this.#basicSnackbarService.open(
                  '코치 매니저 생성에 실패했어요.'
                );
              }
              patchState(this.state$$, {
                content: undefined,
                isLoading: false,
                isLoaded: false,
                errorMessage: extractErrorMessage(e),
              });
            }
          )
        )
      )
    )
  );

  updateExpertManagerWebColorScheme = rxMethod<{
    id: string;
    webColorScheme: WebColorScheme;
    showSuccessSnackbar?: boolean;
    showFailureSnackbar?: boolean;
  }>(
    pipe(
      switchMap(payload =>
        this.#expertManagerService
          .updateExpertManagerWebColorScheme(payload.webColorScheme)
          .pipe(
            tapResponse(
              () => {
                if (payload.showSuccessSnackbar) {
                  switch (payload.webColorScheme) {
                    case WebColorScheme.Light:
                      this.#basicSnackbarService.open(
                        '라이트 모드로 변경되었어요.'
                      );
                      break;
                    case WebColorScheme.Dark:
                      this.#basicSnackbarService.open(
                        '다크 모드로 변경되었어요.'
                      );
                      break;
                    case WebColorScheme.Auto:
                      this.#basicSnackbarService.open(
                        '자동 모드로 변경되었어요.'
                      );
                      break;
                  }
                }
                this.fetchExpertManagerById({
                  id: payload.id,
                  eventType: 'updated',
                });
              },
              (e: HttpErrorResponse) => {
                if (payload.showFailureSnackbar) {
                  this.#basicSnackbarService.open('문제가 발생했어요! 😥');
                }
                patchState(this.state$$, {
                  content: undefined,
                  isLoading: false,
                  isLoaded: false,
                  errorMessage: extractErrorMessage(e),
                });
              }
            )
          )
      )
    )
  );
}
